// Explore › Metrics (APS-198, part B; design "Explore"): one metric of the project per interval,
// picked from the metrics seen in the last week, aggregated (count, rate, sum, average, minimum,
// maximum or a percentile) and optionally split by one field into its 10 largest groups and the
// rest. The API builds the SQL (POST /explore/metrics) and runs it as the project's reader on
// every plan; its SQL can be read here and opened in the SQL tab. The picks live in the address.
// Names, groups and units are the app's data: shown as text, never translated.
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { type FormEvent, useEffect, useId, useState } from 'react'
import { exploreMetricsCatalogQuery, exploreMetricsQuery } from '../api/queries'
import type { ExploreMetricsQuery, ExploreMetrics as Result } from '../api/types'
import { EmptyNote, ScrollX, Section } from '../components/d'
import { LineChart } from '../components/LineChart'
import { Loading } from '../components/states'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { parseLogQuery } from '../lib/logQuery'
import { saveDraft } from './Explore'
import { queryErrorText, RANGE_MS, runError } from './exploreQuery'
import { useProjectParams } from './params'
import {
  type Aggregation,
  EXPLORE_RANGES,
  type ExploreRange,
  type ExploreSearch,
  METRIC_KINDS,
  type MetricKind,
} from './search'

/** As the API takes them (read/explore-metrics.ts): a gauge's sum or rate means nothing. */
export const KIND_AGGREGATIONS: Record<MetricKind, readonly Aggregation[]> = {
  sum: ['sum', 'rate', 'count', 'avg', 'min', 'max', 'p50', 'p90', 'p95', 'p99'],
  gauge: ['avg', 'min', 'max', 'p50', 'p90', 'p95', 'p99', 'count'],
  histogram: ['p90', 'p50', 'p95', 'p99', 'avg', 'count', 'rate', 'sum', 'min', 'max'],
}
/** The chart draws this many lines; the table lists every series. */
const CHART_LINES = 6

/** A UCUM unit as read: `{frame}` is a count of frames. */
const unitText = (unit: string) => unit.replace(/[{}]/g, '')

/** The unit of an aggregate: a count has none, a rate is per minute. */
export function aggregateUnit(aggregation: Aggregation, unit: string): string {
  if (aggregation === 'count') return ''
  if (aggregation === 'rate')
    return unit && !unit.startsWith('{') ? `${unitText(unit)}/min` : '/min'
  return unitText(unit)
}

type Series = Result['series'][number]

function groupName(t: Translator['t'], s: Series): string {
  if (s.other) return t('explore.metrics.other')
  if (s.group === null) return t('explore.metrics.all')
  return s.group === '' ? t('explore.metrics.noValue') : s.group
}

export function ExploreMetrics() {
  const i18n = useI18n()
  const { t, number, date } = i18n
  const { projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as ExploreSearch
  const navigate = useNavigate()
  const range = search.range ?? '24h'
  const [text, setText] = useState(search.q ?? '')
  useEffect(() => setText(search.q ?? ''), [search.q])
  const ids = {
    metric: useId(),
    agg: useId(),
    by: useId(),
    q: useId(),
    range: useId(),
    hint: useId(),
    error: useId(),
  }

  const catalog = useQuery(exploreMetricsCatalogQuery(projectId, search.app))
  const metrics = catalog.data?.metrics ?? []
  const picked =
    metrics.find((m) => m.name === search.metric && (!search.kind || m.kind === search.kind)) ??
    (search.metric ? undefined : metrics[0])
  const kind: MetricKind | undefined = search.kind ?? picked?.kind
  const metric = search.metric ?? picked?.name
  const allowed = kind ? KIND_AGGREGATIONS[kind] : []
  const aggregation =
    search.agg && allowed.includes(search.agg) ? search.agg : (allowed[0] as Aggregation)

  /** A field-only query: severity and body text are the logs'. */
  const read = (words: string) => {
    const parsed = parseLogQuery(words)
    if ('error' in parsed) return { error: queryErrorText(t, parsed.error) }
    if (parsed.query.severity || parsed.query.body)
      return { error: t('explore.metrics.fieldsOnly') }
    return { filters: parsed.query.filters }
  }
  const applied = read(search.q ?? '')
  const typed = read(text)

  const [now] = useState(() => Math.floor(Date.now() / 60_000) * 60_000)
  const body: ExploreMetricsQuery | undefined =
    metric && kind && 'filters' in applied
      ? {
          from: new Date(now - RANGE_MS[range]).toISOString(),
          to: new Date(now + 60_000).toISOString(),
          ...(search.app ? { app_id: search.app } : {}),
          ...(search.build ? { build: search.build } : {}),
          metric,
          kind,
          aggregation,
          ...(search.by ? { group_by: search.by } : {}),
          filters: applied.filters ?? [],
        }
      : undefined
  const result = useQuery(exploreMetricsQuery(projectId, body))

  function go(next: Partial<ExploreSearch>) {
    const merged = {
      metric,
      kind,
      agg: search.agg,
      by: search.by,
      q: search.q,
      range: search.range,
      ...next,
    }
    navigate({
      to: '.',
      search: {
        ...(search.app ? { app: search.app } : {}),
        tab: 'metrics' as const,
        ...(merged.metric ? { metric: merged.metric } : {}),
        ...(merged.kind ? { kind: merged.kind } : {}),
        ...(merged.agg ? { agg: merged.agg } : {}),
        ...(merged.by ? { by: merged.by } : {}),
        ...(merged.q?.trim() ? { q: merged.q.trim() } : {}),
        ...(merged.range && merged.range !== '24h' ? { range: merged.range } : {}),
      },
    })
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    if ('error' in typed) return
    go({ q: text })
  }

  const data = result.data
  const unit = data ? aggregateUnit(data.aggregation, data.unit) : ''
  const format = (v: number) =>
    `${number(v, { maximumFractionDigits: Math.abs(v) < 10 ? 2 : 0 })}${unit ? ` ${unit}` : ''}`
  const labels = (data?.series[0]?.points ?? []).map((p) =>
    (data?.bucket_seconds ?? 0) >= 86_400
      ? date(p.t, { month: 'short', day: 'numeric' })
      : date(p.t, { hour: '2-digit', minute: '2-digit' }),
  )
  const lines = (data?.series ?? []).slice(0, CHART_LINES)
  const [head, ...rest] = lines
  const stats = (s: Series) => {
    const values = s.points.map((p) => p.value).filter((v): v is number => v !== null)
    return {
      last: values.at(-1),
      avg: values.length ? values.reduce((a, b) => a + b, 0) / values.length : undefined,
      max: values.length ? Math.max(...values) : undefined,
    }
  }
  const cell = (v: number | undefined) => (v === undefined ? '—' : format(v))

  return (
    <div className="cols">
      <div className="col-main">
        <Section title={t('explore.metrics.query')}>
          {catalog.isPending ? (
            <Loading />
          ) : metrics.length === 0 ? (
            <EmptyNote title={t('explore.metrics.noMetrics')}>
              {t('explore.metrics.noMetricsBody')}
            </EmptyNote>
          ) : (
            <form className="form pb" onSubmit={submit}>
              <div className="form-row" style={{ alignItems: 'flex-end' }}>
                <div className="field" style={{ flex: '1 1 260px' }}>
                  <label htmlFor={ids.metric}>{t('explore.metrics.metric')}</label>
                  <select
                    id={ids.metric}
                    className="input mono"
                    translate="no"
                    value={metric && kind ? `${kind}|${metric}` : ''}
                    onChange={(e) => {
                      const [k, ...name] = e.target.value.split('|')
                      go({
                        kind: k as MetricKind,
                        metric: name.join('|'),
                        agg: undefined,
                        by: undefined,
                      })
                    }}
                  >
                    {METRIC_KINDS.map((k) => {
                      const list = metrics.filter((m) => m.kind === k)
                      return list.length ? (
                        <optgroup key={k} label={t(`explore.metrics.kind.${k}` as MessageId)}>
                          {list.map((m) => (
                            <option key={`${k}|${m.name}`} value={`${k}|${m.name}`}>
                              {m.name}
                            </option>
                          ))}
                        </optgroup>
                      ) : null
                    })}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor={ids.agg}>{t('explore.metrics.aggregation')}</label>
                  <select
                    id={ids.agg}
                    className="input"
                    value={aggregation ?? ''}
                    onChange={(e) => go({ agg: e.target.value as Aggregation })}
                  >
                    {allowed.map((a) => (
                      <option key={a} value={a}>
                        {t(`explore.metrics.agg.${a}` as MessageId)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor={ids.by}>{t('explore.metrics.groupBy')}</label>
                  <select
                    id={ids.by}
                    className="input mono"
                    translate="no"
                    value={search.by ?? ''}
                    onChange={(e) => go({ by: e.target.value || undefined })}
                  >
                    <option value="">{t('explore.metrics.groupNone')}</option>
                    <optgroup label={t('explore.metrics.groupFields')}>
                      {(catalog.data?.fields ?? []).map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </optgroup>
                    {picked?.attributes.length ? (
                      <optgroup label={t('explore.metrics.groupAttributes')}>
                        {picked.attributes.map((f) => (
                          <option key={f} value={f}>
                            {f}
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor={ids.range}>{t('explore.logs.range')}</label>
                  <select
                    id={ids.range}
                    className="input"
                    value={range}
                    onChange={(e) => go({ range: e.target.value as ExploreRange })}
                  >
                    {EXPLORE_RANGES.map((r) => (
                      <option key={r} value={r}>
                        {t(`explore.logs.range.${r}` as MessageId)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row" style={{ alignItems: 'flex-end' }}>
                <div className="field" style={{ flex: '1 1 320px' }}>
                  <label htmlFor={ids.q}>{t('explore.metrics.filters')}</label>
                  <input
                    id={ids.q}
                    type="search"
                    className="input mono"
                    style={{ maxWidth: 'none' }}
                    spellCheck={false}
                    autoCapitalize="off"
                    autoCorrect="off"
                    translate="no"
                    maxLength={1000}
                    placeholder='service.version:"5.2" -os.name:Android'
                    aria-describedby={'error' in typed ? ids.error : ids.hint}
                    aria-invalid={'error' in typed || undefined}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                </div>
                <button type="submit" className="btn solid" disabled={'error' in typed}>
                  {t('explore.run')}
                </button>
              </div>
              {'error' in typed ? (
                <p id={ids.error} role="alert" className="notice error">
                  {typed.error}
                </p>
              ) : (
                <span id={ids.hint} className="hint">
                  {t('explore.metrics.hint')}
                </span>
              )}
            </form>
          )}
        </Section>

        {metrics.length === 0 && !catalog.isPending ? null : (
          <Section
            title={t('explore.metrics.chart')}
            meta={data ? t('explore.logs.volumeSub', { seconds: data.bucket_seconds }) : undefined}
            right={
              data ? (
                <button
                  type="button"
                  className="btn small"
                  onClick={() => {
                    saveDraft(projectId, data.sql)
                    navigate({
                      to: '.',
                      search: search.app ? { app: search.app } : {},
                    })
                  }}
                >
                  {t('explore.logs.openSql')}
                </button>
              ) : undefined
            }
          >
            {!body || result.isPending ? (
              catalog.isPending || body ? (
                <Loading />
              ) : null
            ) : result.isError ? (
              <div className="pb">
                <p role="alert" className="notice error">
                  {runError(t, result.error)}
                </p>
              </div>
            ) : !data || !head ? (
              <EmptyNote title={t('explore.metrics.none')}>
                {t('explore.metrics.noneBody')}
              </EmptyNote>
            ) : (
              <>
                <div style={{ padding: '14px 18px 10px' }}>
                  <LineChart
                    label={t('explore.metrics.chartLabel', {
                      metric: data.metric,
                      aggregation: t(`explore.metrics.agg.${data.aggregation}` as MessageId),
                    })}
                    points={head.points.map((p, i) => ({ label: labels[i] ?? '', value: p.value }))}
                    name={groupName(t, head)}
                    more={rest.map((s) => ({
                      name: groupName(t, s),
                      values: s.points.map((p) => p.value),
                    }))}
                    format={format}
                  />
                  {data.series.length > CHART_LINES ? (
                    <p className="hint">{t('explore.metrics.chartTop', { lines: CHART_LINES })}</p>
                  ) : null}
                </div>
                <ScrollX label={t('explore.metrics.valuesTable')}>
                  <table className="t">
                    <thead>
                      <tr>
                        <th scope="col">{t('explore.metrics.col.group')}</th>
                        <th scope="col" className="num">
                          {t('explore.metrics.col.last')}
                        </th>
                        <th scope="col" className="num">
                          {t('explore.metrics.col.avg')}
                        </th>
                        <th scope="col" className="num">
                          {t('explore.metrics.col.max')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.series.map((s) => {
                        const v = stats(s)
                        return (
                          <tr key={s.other ? '\u0000other' : (s.group ?? '\u0000all')}>
                            <th
                              scope="row"
                              className="mono"
                              translate={s.group !== null && s.group !== '' ? 'no' : undefined}
                            >
                              {groupName(t, s)}
                            </th>
                            <td className="num">{cell(v.last)}</td>
                            <td className="num">{cell(v.avg)}</td>
                            <td className="num">{cell(v.max)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </ScrollX>
              </>
            )}
            {data ? (
              <details className="pb">
                <summary>{t('explore.logs.sql')}</summary>
                <div className="code" translate="no">
                  <pre style={{ whiteSpace: 'pre-wrap' }}>{data.sql}</pre>
                </div>
              </details>
            ) : null}
          </Section>
        )}
      </div>
    </div>
  )
}
